import { getApi } from "./get-api";
import type { SheetsHealth } from "./sheets-health";

/**
 * MOSA-409. Client for the two connection-health routes.
 *
 * - GET  /datasources/:datasourceId/health
 * - POST /datasources/:datasourceId/sheets/columns/map
 *
 * Both are newer than the checked-in swagger, so they go through the generated
 * client's escape hatch the way `sync-api.ts` does, rather than waiting on a
 * regenerated `api-client.ts`.
 */

/**
 * Ask the API to check the connection.
 *
 * A failure here is not a health problem: the panel must not claim the sheet
 * is disconnected because our own request did not arrive. The caller gets null
 * and shows nothing.
 */
export async function fetchSheetsHealth(
  token: string | null | undefined,
  datasourceId: string,
): Promise<SheetsHealth | null> {
  try {
    const response = await (getApi({ token }) as any).request({
      path: `/datasources/${encodeURIComponent(datasourceId)}/health`,
      method: "GET",
      format: "json",
    });
    return (response?.data as SheetsHealth) ?? null;
  } catch {
    return null;
  }
}

/**
 * The owner's answer to the renamed-column notice.
 *
 * This one throws. The owner pressed a button and is owed either the new state
 * or an error line, unlike the check above, which runs on its own.
 */
export async function mapSheetsColumns(
  token: string | null | undefined,
  datasourceId: string,
  tab: string,
  choice: "map" | "keep_both",
): Promise<SheetsHealth> {
  const response = await (getApi({ token }) as any).request({
    path: `/datasources/${encodeURIComponent(datasourceId)}/sheets/columns/map`,
    method: "POST",
    body: { tab, choice },
    format: "json",
  });
  return response?.data as SheetsHealth;
}
